import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Window, Button, Select, TextField, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@warmancer/ui';
import { requestLogsApi, type RequestLog } from '../api/client';
import { useBlockingMutation } from '../hooks/useBlockingMutation';
import { useAdminStore } from '../stores/adminStore';
import { formatTimestamp } from '../utils/formatTimestamp';
import styles from './RequestLogs.module.css';

const workerOptions = [
  { value: '', label: 'All Workers' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'admin', label: 'Admin' },
  { value: 'api', label: 'API' },
];

const methodOptions = [
  { value: '', label: 'All Methods' },
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
];

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: '2xx', label: '2xx Success' },
  { value: '4xx', label: '4xx Client' },
  { value: '5xx', label: '5xx Server' },
];

const searchTypeOptions = [
  { value: 'ip', label: 'IP Address' },
  { value: 'account', label: 'Account' },
  { value: 'fingerprint', label: 'Fingerprint' },
];

function getStatusClass(status: number): string {
  if (status >= 200 && status < 300) return styles.status2xx;
  if (status >= 300 && status < 400) return styles.status3xx;
  if (status >= 400 && status < 500) return styles.status4xx;
  if (status >= 500) return styles.status5xx;
  return '';
}

function getMethodClass(method: string): string {
  switch (method) {
    case 'GET': return styles.methodGET;
    case 'POST': return styles.methodPOST;
    case 'PATCH': return styles.methodPATCH;
    case 'DELETE': return styles.methodDELETE;
    default: return '';
  }
}

function getStatusRange(value: string): { min?: string; max?: string } {
  switch (value) {
    case '2xx': return { min: '200', max: '299' };
    case '4xx': return { min: '400', max: '499' };
    case '5xx': return { min: '500', max: '599' };
    default: return {};
  }
}

export function RequestLogs() {
  const queryClient = useQueryClient();
  const { hasRole } = useAdminStore();

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('ip');

  // Filter state
  const [worker, setWorker] = useState('');
  const [method, setMethod] = useState('');
  const [statusRange, setStatusRange] = useState('');

  // Active filters (applied on search)
  const [activeSearch, setActiveSearch] = useState<{ type: string; term: string }>({ type: 'ip', term: '' });
  const [activeFilters, setActiveFilters] = useState<{
    worker: string; method: string; statusRange: string;
  }>({ worker: '', method: '', statusRange: '' });

  const [page, setPage] = useState(0);
  const [selectedLog, setSelectedLog] = useState<RequestLog | null>(null);
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
  const limit = 50;

  const buildQueryParams = useCallback(() => {
    const { min: statusMin, max: statusMax } = getStatusRange(activeFilters.statusRange);
    return {
      ip: activeSearch.type === 'ip' && activeSearch.term ? activeSearch.term : undefined,
      account: activeSearch.type === 'account' && activeSearch.term ? activeSearch.term : undefined,
      fingerprint: activeSearch.type === 'fingerprint' && activeSearch.term ? activeSearch.term : undefined,
      worker: activeFilters.worker || undefined,
      method: activeFilters.method || undefined,
      status_min: statusMin,
      status_max: statusMax,
      limit,
      offset: page * limit,
    };
  }, [activeSearch, activeFilters, page, limit]);

  const { data: stats } = useQuery({
    queryKey: ['request-log-stats'],
    queryFn: () => requestLogsApi.getStats(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['request-logs', activeSearch, activeFilters, page],
    queryFn: () => requestLogsApi.list(buildQueryParams()),
  });

  const cleanupMutation = useBlockingMutation({
    loadingMessage: 'Cleaning up old logs...',
    mutationFn: () => requestLogsApi.cleanup(90),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request-logs'] });
      queryClient.invalidateQueries({ queryKey: ['request-log-stats'] });
      setShowCleanupConfirm(false);
      setPage(0);
    },
  });

  const handleSearch = useCallback(() => {
    setActiveSearch({ type: searchType, term: searchTerm });
    setActiveFilters({ worker, method, statusRange });
    setPage(0);
  }, [searchType, searchTerm, worker, method, statusRange]);

  const downloadJson = useCallback((jsonData: unknown, filename: string) => {
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const [exportError, setExportError] = useState('');

  const handleExportPage = useCallback(() => {
    try {
      if (!data?.logs) return;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      downloadJson(data.logs, `warmancer-request-logs-page-${timestamp}.json`);
    } catch {
      setExportError('Failed to export logs');
    }
  }, [data?.logs, downloadJson]);

  const handleExportAll = useCallback(async () => {
    try {
      const params = buildQueryParams();
      const result = await requestLogsApi.exportAll(params);
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      downloadJson(result.logs, `warmancer-request-logs-all-${timestamp}.json`);
    } catch {
      setExportError('Failed to export logs');
    }
  }, [buildQueryParams, downloadJson]);

  const totalPages = Math.ceil((data?.pagination.total || 0) / limit);
  const fiveXxCount = stats?.byStatus?.find((s) => s.status_group === '5xx')?.count || 0;

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Request Logs</h1>

      <div className={styles.statsRow}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Total Requests</span>
          <span className={styles.statValue}>{stats?.total || 0}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Avg Duration</span>
          <span className={styles.statValue}>{stats?.avgDuration || 0}ms</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>5xx Errors</span>
          <span className={`${styles.statValue} ${styles.statDanger}`}>
            {fiveXxCount}
          </span>
        </div>
      </div>

      <div className={styles.searchRow}>
        <div className={styles.searchInput}>
          <TextField
            label="Search"
            name="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          />
        </div>
        <div className={styles.searchType}>
          <Select
            name="searchType"
            options={searchTypeOptions}
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
          />
        </div>
        <button className={styles.searchBtn} onClick={handleSearch}>
          Search
        </button>
      </div>

      <div className={styles.filtersRow}>
        <div className={styles.filters}>
          <Select
            name="worker"
            options={workerOptions}
            value={worker}
            onChange={(e) => setWorker(e.target.value)}
          />
          <Select
            name="method"
            options={methodOptions}
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          />
          <Select
            name="status"
            options={statusOptions}
            value={statusRange}
            onChange={(e) => setStatusRange(e.target.value)}
          />
        </div>
        <div className={styles.bulkActions}>
          {hasRole('super') && (
            <button
              className={`${styles.actionBtn} ${styles.dangerBtn}`}
              onClick={() => setShowCleanupConfirm(true)}
            >
              Cleanup (90d)
            </button>
          )}
          {exportError && (
            <span style={{ color: '#ff4444', fontSize: 12 }}>{exportError}</span>
          )}
          <button
            className={styles.actionBtn}
            onClick={handleExportPage}
            disabled={!data?.logs?.length}
          >
            Export Page
          </button>
          <button
            className={styles.actionBtn}
            onClick={handleExportAll}
          >
            Export All
          </button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Timestamp</TableHeader>
              <TableHeader>Worker</TableHeader>
              <TableHeader>Method</TableHeader>
              <TableHeader>Path</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Duration</TableHeader>
              <TableHeader>Account</TableHeader>
              <TableHeader>IP</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>
                  <span className={styles.timestamp}>
                    {formatTimestamp(log.created_at)}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.worker}>{log.worker}</span>
                </TableCell>
                <TableCell>
                  <span className={`${styles.method} ${getMethodClass(log.method)}`}>
                    {log.method}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.path} title={log.path}>
                    {log.path}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={getStatusClass(log.status_code)}>
                    {log.status_code}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.duration}>{log.duration_ms}ms</span>
                </TableCell>
                <TableCell>
                  <span className={styles.account}>
                    {log.account_username || log.account_id || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.ip}>{log.ip_address || '-'}</span>
                </TableCell>
                <TableCell>
                  <button
                    className={styles.viewBtn}
                    onClick={() => setSelectedLog(log)}
                  >
                    View
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {isLoading && <div className={styles.loading}>Loading...</div>}
        {!isLoading && !data?.logs.length && (
          <div className={styles.empty}>No request logs found</div>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            className={styles.pageBtn}
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Cleanup Confirmation Modal */}
      {showCleanupConfirm && (
        <div className={styles.modalOverlay} onClick={() => setShowCleanupConfirm(false)}>
          <Window
            title="Cleanup Request Logs"
            maxWidth={400}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <div className={styles.logDetail}>
              <p style={{ color: '#c0c0c0', fontSize: 14, margin: 0 }}>
                This will permanently delete all request logs older than{' '}
                <strong style={{ color: '#ff4444' }}>90 days</strong>.
              </p>
              <div className={styles.detailActions}>
                <Button onClick={() => setShowCleanupConfirm(false)}>Cancel</Button>
                <Button
                  onClick={() => cleanupMutation.mutate()}
                  loading={cleanupMutation.isPending}
                >
                  Cleanup
                </Button>
              </div>
            </div>
          </Window>
        </div>
      )}

      {/* Log Detail Modal */}
      {selectedLog && (
        <div className={styles.modalOverlay} onClick={() => setSelectedLog(null)}>
          <Window
            title="Request Log Details"
            maxWidth={600}
            onClose={() => setSelectedLog(null)}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <div className={styles.logDetail}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>ID</span>
                <span className={styles.detailValue}>{selectedLog.id}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Worker</span>
                <span className={styles.worker}>{selectedLog.worker}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Method</span>
                <span className={`${styles.method} ${getMethodClass(selectedLog.method)}`}>
                  {selectedLog.method}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Path</span>
                <span className={styles.detailValue}>{selectedLog.path}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Status</span>
                <span className={getStatusClass(selectedLog.status_code)}>
                  {selectedLog.status_code}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Duration</span>
                <span className={styles.detailValue}>{selectedLog.duration_ms}ms</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Timestamp</span>
                <span className={styles.detailValue}>
                  {formatTimestamp(selectedLog.created_at)}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>IP Address</span>
                <span className={styles.detailValue}>{selectedLog.ip_address || '-'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Account</span>
                <span className={styles.detailValue}>
                  {selectedLog.account_username
                    ? `${selectedLog.account_username} (${selectedLog.account_id})`
                    : selectedLog.account_id || '-'}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Fingerprint</span>
                <span className={styles.detailValue}>{selectedLog.fingerprint || '-'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>User Agent</span>
                <span className={styles.detailValue}>{selectedLog.user_agent || '-'}</span>
              </div>
              {selectedLog.error_code && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Error Code</span>
                  <span className={styles.detailValue} style={{ color: '#ff4444' }}>
                    {selectedLog.error_code}
                  </span>
                </div>
              )}
              {selectedLog.metadata && (
                <div className={styles.detailSection}>
                  <span className={styles.detailLabel}>Metadata</span>
                  <pre className={styles.detailPre}>
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </Window>
        </div>
      )}
    </div>
  );
}
